import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
	Menu,
	MenuItem,
	Box,
	TextField,
	Typography,
	Checkbox,
	Button,
} from '@mui/material';
import { connect } from 'react-redux';
import { DARK_MODE } from '../../constants';
import ClearIcon from '@mui/icons-material/Clear';
import { getScaleFactor } from '../../theme/resolutionConfig';

const getPaperSx = (width) => ({
	minWidth: width,
	maxWidth: width,
	background: 'inherit',
	border: 'none',
	borderRadius: '8px',
});

const FilterMenu = ({
	open,
	handleClose,
	anchorEl,
	columns,
	rows,
	filters,
	onFilterChange,
	themeMode,
	getFilteredDataForField,
}) => {
	const [filterSearch, setFilterSearch] = useState('');
	const [localFilters, setLocalFilters] = useState(filters || {});

	useEffect(() => {
		setLocalFilters(filters || {});
	}, [filters]);

	const getFilterOptions = useCallback(
		(field) => {
			// Use cascading filtering if available, otherwise fall back to all rows
			const dataSource = getFilteredDataForField
				? getFilteredDataForField(field)
				: rows;
			const values = dataSource.map((row) => row[field]).filter(Boolean);
			return [...new Set(values)].sort();
		},
		[rows, getFilteredDataForField],
	);

	const handleFilterChange = (field, values) => {
		const newFilters = {
			...localFilters,
			[field]: values,
		};
		setLocalFilters(newFilters);
		onFilterChange(newFilters);
	};

	const clearAllFilters = () => {
		setLocalFilters({});
		onFilterChange({});
	};

	const filterableColumns = columns.filter((col) => col.filterable !== false);

	// Get scale factor based on screen width (reduced by 20%)
	const scaleFactor = getScaleFactor(window.innerWidth) * 0.8;
	const baseWidth = 420;
	const baseMaxWidth = 480;
	const baseHeight = 500;
	const basePadding = 24; // 3 * 8px

	return (
		<Menu
			anchorEl={anchorEl}
			open={open}
			onClose={handleClose}
			PaperProps={{
				sx: {
					minWidth: baseWidth * scaleFactor,
					maxWidth: baseMaxWidth * scaleFactor,
					elevation: 8,
					maxHeight: baseHeight * scaleFactor,
					overflowY: 'auto',
					background:
						themeMode === DARK_MODE
							? 'linear-gradient(115.48deg, #1A2127 31.9%, #151A1F 100%)'
							: '#F2F2F2',
					borderRadius: `${12 * scaleFactor}px`,
					marginTop: `${8 * scaleFactor}px`,
				},
			}}
			MenuListProps={{ sx: { p: 0 } }}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'right',
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
		>
			<Box p={`${basePadding * scaleFactor}px`} minWidth={380 * scaleFactor}>
				<Typography
					variant="subtitle1"
					sx={{
						mb: 2,
						fontSize: `${1.1 * scaleFactor}rem`,
						fontWeight: 600,
						color: themeMode === DARK_MODE ? '#F8F8F8' : '#111827',
					}}
				>
					Filter Options
				</Typography>

				<TextField
					fullWidth
					size="medium"
					placeholder="Search filters..."
					value={filterSearch}
					onChange={(e) => setFilterSearch(e.target.value)}
					sx={{
						mb: 2,
						'& .MuiOutlinedInput-root': {
							color: themeMode === DARK_MODE ? '#F8F8F8' : '#111827',
							fontSize: `${1.1 * scaleFactor}rem`,
							fontWeight: 600,
							'& fieldset': {
								borderColor:
									themeMode === DARK_MODE ? 'rgba(229,231,235,0.3)' : '#CBD5E1',
							},
							'&:hover fieldset': {
								borderColor: themeMode === DARK_MODE ? '#94A3B8' : '#475569',
							},
							'&.Mui-focused fieldset': {
								borderColor: themeMode === DARK_MODE ? '#60a5fa' : '#2563EB',
							},
						},
						'& .MuiInputBase-input': {
							color: themeMode === DARK_MODE ? '#F8F8F8' : '#111827',
							padding: `${12 * scaleFactor}px ${14 * scaleFactor}px`,
						},
					}}
				/>

				{filterableColumns.map((column) => {
					const fieldKey = column?.field || column?.key;
					const options = getFilterOptions(fieldKey);
					const current = localFilters[fieldKey] || [];
					const shown = filterSearch
						? options.filter((o) =>
								o.toString().toLowerCase().includes(filterSearch.toLowerCase()),
						  )
						: options;

					if (shown.length === 0) return null;

					return (
						<Box key={fieldKey} mb={2.5}>
							<Typography
								variant="body1"
								gutterBottom
								sx={{
									color: themeMode === DARK_MODE ? '#F8F8F8' : '#111827',
									fontWeight: 600,
									fontSize: `${1.1 * scaleFactor}rem`,
									mb: 1,
								}}
							>
								{column.label || column.title || fieldKey}
							</Typography>

							{shown.map((opt) => (
								<MenuItem
									key={opt}
									onClick={() => {
										const newVals = current.includes(opt)
											? current.filter((v) => v !== opt)
											: [...current, opt];
										handleFilterChange(fieldKey, newVals);
									}}
									sx={{
										borderRadius: `${8 * scaleFactor}px`,
										mx: 0,
										py: `${12 * scaleFactor}px`,
										fontSize: `${1.1 * scaleFactor}rem`,
										fontWeight: 600,
										color: themeMode === DARK_MODE ? '#F8F8F8' : '#111827',
										'&:hover': {
											backgroundColor:
												themeMode === DARK_MODE
													? 'rgba(255,255,255,0.04)'
													: 'rgba(0,0,0,0.04)',
										},
									}}
								>
									<Checkbox
										checked={current.includes(opt)}
										sx={{
											color: themeMode === DARK_MODE ? '#374151' : '#9CA3AF',
											'&.Mui-checked': {
												color:
													themeMode === DARK_MODE ? '#60a5fa' : '#2331afff',
											},
											'& .MuiSvgIcon-root': {
												fontSize: `${1.4 * scaleFactor}rem`,
											},
										}}
									/>
									{opt}
								</MenuItem>
							))}

							{current.length > 0 && (
								<Button
									size="medium"
									startIcon={
										<ClearIcon sx={{ fontSize: `${20 * scaleFactor}px` }} />
									}
									onClick={() => handleFilterChange(fieldKey, [])}
									sx={{
										mt: 1,
										fontSize: `${0.9 * scaleFactor}rem`,
										color: themeMode === DARK_MODE ? '#F87171' : '#DC2626',
										'&:hover': {
											backgroundColor:
												themeMode === DARK_MODE
													? 'rgba(248,113,113,0.1)'
													: 'rgba(220,38,38,0.1)',
										},
									}}
								>
									Clear
								</Button>
							)}
						</Box>
					);
				})}

				{Object.keys(localFilters).length > 0 && (
					<Box
						mt={2}
						pt={2}
						borderTop={`${1 * scaleFactor}px solid ${
							themeMode === DARK_MODE
								? 'rgba(255, 255, 255, 0.1)'
								: 'rgba(0, 0, 0, 0.12)'
						}`}
					>
						<Button
							size="medium"
							startIcon={
								<ClearIcon sx={{ fontSize: `${20 * scaleFactor}px` }} />
							}
							onClick={clearAllFilters}
							sx={{
								fontSize: `${0.95 * scaleFactor}rem`,
								fontWeight: 500,
								color: themeMode === DARK_MODE ? '#F87171' : '#DC2626',
								'&:hover': {
									backgroundColor:
										themeMode === DARK_MODE
											? 'rgba(248,113,113,0.1)'
											: 'rgba(220,38,38,0.1)',
								},
							}}
						>
							Clear All Filters
						</Button>
					</Box>
				)}
			</Box>
		</Menu>
	);
};

FilterMenu.propTypes = {
	open: PropTypes.bool.isRequired,
	handleClose: PropTypes.func.isRequired,
	anchorEl: PropTypes.instanceOf(Element),
	columns: PropTypes.array.isRequired,
	rows: PropTypes.array.isRequired,
	filters: PropTypes.object,
	onFilterChange: PropTypes.func.isRequired,
	themeMode: PropTypes.string,
	getFilteredDataForField: PropTypes.func,
};

const mapStateToProps = (state) => ({
	themeMode: state.theme.themeMode,
});

export default connect(mapStateToProps)(FilterMenu);
